import { Modal, Stack, Text } from '@mantine/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PublicationAddNewAliasModal } from '@/module/Author/Publication/components/PublicationAddNewAliasModal';

import { AddButtonLink } from '@/core/components';

import { Nomenclature } from '@feature/nomenclature';
import { useCurrentUser } from '@feature/security';
import { Id } from '@feature/utils';
import { ReclaimableAuthorList } from '@feature/utils/components/ReclaimableAuthorList';

import { PublicationNewAliasRequest, PublicationReclaimSearch, ReclaimRequest } from './model/PublicationTypes';

interface PublicationFoundModalProps {
  close: () => void;
  data: PublicationReclaimSearch;

  onReclaimAuthorship: (v: ReclaimRequest) => void;
  memberships?: Nomenclature[];
  membershipsLoading: boolean;
  membershipsError: boolean;
  onAddNewAlias?: (publicationId: Id, request: PublicationNewAliasRequest) => void;
}

export function PublicationFoundModal({
  close,
  data,
  onReclaimAuthorship,
  memberships,
  membershipsLoading,
  membershipsError,
  onAddNewAlias,
}: PublicationFoundModalProps) {
  const { publication } = data;
  const { t } = useTranslation();
  const user = useCurrentUser();

  const showNewAliasModal = useMemo(
    () => !publication?.authors.some(author => author.user?.id === user.id),
    [publication?.authors, user.id],
  );

  const onSubmit = (request: PublicationNewAliasRequest) => {
    if (onAddNewAlias && publication) {
      onAddNewAlias(publication.id, request);
    }
  };

  return (
    <Modal
      opened
      onClose={close}
      centered
      size="lg"
      title={publication != null && t('publications.foundPub')}
    >
      {publication == null && (
        <Stack>
          <Text ta="center">{t('publications.notSearchFound')}</Text>
          <AddButtonLink
            to="../create"
            name={t('button.addNew')}
          />
        </Stack>
      )}
      {publication != null && (
        <Stack>
          <Text fw="500">{publication.title}</Text>
          <ReclaimableAuthorList
            authors={publication.authors}
            onReclaim={authorId => onReclaimAuthorship({ authorId, publicationId: publication.id })}
          />
          {publication && showNewAliasModal && memberships && onAddNewAlias && (
            <PublicationAddNewAliasModal
              publication={publication}
              memberships={memberships}
              membershipsLoading={membershipsLoading}
              membershipsError={membershipsError}
              onSubmit={onSubmit}
            />
          )}
        </Stack>
      )}
    </Modal>
  );
}
